import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RegisterType } from 'types';

import * as S from '../../Order.styles';

type OrderNoteFieldProps = Readonly<{
  name: string;
  label: string;
  /** Initial text; the field is remounted (`key`) when the order changes. */
  value: string;
  maxLength: number;
  register: RegisterType;
  disabled: boolean;
  /** Full-height field; otherwise one line. */
  expanded: boolean;
  /** Only the label is shown (the field stays mounted, so typed text is kept). */
  hidden?: boolean;
  onChange: (value: string) => void;
  'data-testid'?: string;
}>;

export function OrderNoteField({
  name,
  label,
  value,
  maxLength,
  register,
  disabled,
  expanded,
  hidden = false,
  onChange,
  'data-testid': testId,
}: OrderNoteFieldProps) {
  const { t } = useTranslation();
  const [length, setLength] = useState(value.length);
  const isOver = length > maxLength;

  // The text can arrive after mount (the order's notes are synced into the form in an effect),
  // so follow `value` instead of counting it only once.
  useEffect(() => {
    setLength(value.length);
  }, [value]);

  return (
    <S.NoteField data-testid={testId}>
      <S.NoteArea
        register={register}
        name={name}
        value={value}
        label={label}
        disabled={disabled}
        // Typing or pasting past the limit is cut off, so an over-long note cannot be sent.
        maxLength={maxLength}
        onChange={(e) => {
          setLength(e.target.value.length);
          onChange(e.target.value);
        }}
        $expanded={expanded}
        $invalid={isOver}
        $hidden={hidden}
      />
      {hidden ? null : (
        <S.NoteCounter $over={isOver} aria-live="polite">
          {isOver ? `${t('order.noteTooLong')} · ` : null}
          {length} / {maxLength}
        </S.NoteCounter>
      )}
    </S.NoteField>
  );
}
